import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'components/patterns/Modal'
import strings from 'utils/strings'
import withStyle from './ModalWizard.style.js'
import 'styles/ModalWizard.css'

/**
 * A wizard is described by providing Steps. Steps are objects that provide the configuration
 * for each step of the wizard.
 * Step: {
 *  stepName: Name of the step. Displayed at the top of the modal while this step is current. Concatted with the processName prop. Required
 *  nextButtonText: Text to display on the button that advances the wizard. Optional. Default: 'Next'
 *  backButtonText: Text to display on the button that moves the wizard back a step. Optional. Default: 'Back'
 *  nextHandler: Function to execute on click of Next button. Receives no arguments. Optional.
 *  backHandler: Function to execute on click of the Back button  Receives no arguments. Optional.
 *  nextDisabled: Boolean function that indicates whether or not the Next button should be disabled. Optional.
 *  backDisabled: Boolean function that indicates whether or not the Back button should be disabled. Optional.
 *  element: A React element. Serves as the content for that step. Required.
 * }
 *
 * Steps are provided in the steps prop as an array of objects. The steps are carried out in index order.
 */

class ModalWizard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      currStepIdx: 0,
      isOpen: true,
    }
  }

  lastStep = () => this.state.currStepIdx === this.props.steps.length - 1

  nextHandler = () => {
    const { steps } = this.props
    const { currStepIdx } = this.state

    if (steps[currStepIdx].nextHandler) {
      steps[currStepIdx].nextHandler()
    }

    if (currStepIdx < steps.length - 1) {
      this.setState({ currStepIdx: currStepIdx + 1 })
    } else {
      this.setState({ isOpen: false })
    }
  }

  backHandler = () => {
    const { steps } = this.props
    const { currStepIdx } = this.state

    if (steps[currStepIdx].backHandler) {
      steps[currStepIdx].backHandler()
    }

    if (currStepIdx > 0) {
      this.setState({ currStepIdx: currStepIdx - 1 })
    }
  }

  render() {
    const { steps, processName, allowCancel, cancelHandler } = this.props

    const { currStepIdx, isOpen } = this.state

    const currStep = steps[currStepIdx]

    return (
      <Modal
        customButtons={[
          {
            text: currStep.backButtonText || strings.back,
            onClick: this.backHandler,
            disabled: currStepIdx === 0,
          },
          {
            text:
              currStep.nextButtonText ||
              (this.lastStep() ? strings.close : strings.next),
            onClick: this.nextHandler,
            disabled: currStep.nextDisabled ? currStep.nextDisabled() : false,
            type: 'primary',
          },
        ]}
        showCancel={allowCancel}
        cancelBelow
        cancelHandler={cancelHandler}
        header={`${processName ? `${processName}: ` : ''}${currStep.stepName}`}
        isOpen={isOpen}
      >
        <div>
          {steps[currStepIdx] && steps[currStepIdx].element}
          <div className={'modal-wizard_step-description'}>
            <span>{strings.step_x_of_y(currStepIdx + 1, steps.length)}</span>
          </div>
        </div>
      </Modal>
    )
  }
}

ModalWizard.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      disabled: PropTypes.bool,
      type: PropTypes.string,
      nextHandler: PropTypes.func,
      backHandler: PropTypes.func,
      nextButtonText: PropTypes.string,
      backButtonText: PropTypes.string,
      nextDisabled: PropTypes.bool,
      stepName: PropTypes.string,
      element: PropTypes.node,
    })
  ),
  processName: PropTypes.string,
  allowCancel: PropTypes.bool, // allow the user to cancel the wizard?
  cancelHandler: PropTypes.func, // executed on Cancel. Optional.
  cancelBelow: PropTypes.bool,
  isOpen: PropTypes.bool,
}

export default withStyle(ModalWizard)
